// features/item-filter/useItemFilter.js
//
// Single owner of the item search filter for a page. State model:
//
//   committed  — the active filter, a filterValue object, tab-persisted.
//                Drives every data fetch (grid, chart, export).
//   inputText  — the draft the user is typing. Plain component state,
//                never persisted; initialized from the committed filter's
//                display text so restore always shows what is applied.
//
// Everything else (suggestions, loading) is derived. There is no third
// "selected item" slot: a picked item commits its label into the filter object
// itself (see filterValue.js), so a restore needs no fetch. The one read is
// for an item committed by code alone (a route-segment seed), whose label is
// fetched once and then persisted with the filter.
//
// Mutations funnel through ONE guarded commit path. `beforeChange` (the
// unsaved-grid-edits confirm on editable pages) runs exactly once per user
// action, before any state moves; a falsy return leaves both the committed
// filter and the input untouched. Pages must not gate their setters again.
// `guarded()` is exported so ItemFilterBar routes the adjacent location /
// product-category setters through the same choke point.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTabPersistedState } from '../../tabs/useTabPersistedState';
import { sessionApi } from '../../services/SessionApiService';
import { apiErrorFrom } from '../../utils/parseApiError';
import { useItemSuggestions } from './useItemSuggestions';
import { useIsShopify } from '../../contexts/AuthContext';
import {
  textFilter, itemFilter, itemCodeFilter, filterScope, filterParams, filterDisplayText,
} from './filterValue';

export function useItemFilter({
  persistKey = 'itemFilter',
  // The filter a NEW tab starts with — one item's item_code, from a route
  // segment (/item-setup/item/:itemCode, /suppliers/:id/catalog/:itemCode).
  // It filters at once; the box shows the item's name once one request has
  // fetched it.
  //
  // A tab that already holds a filter — including one the merchant cleared —
  // keeps it: the seed stays in the tab path, so F5 must not undo what the
  // merchant did since (the vendor and Supplier catalog drill-throughs keep
  // the same contract).
  //
  // Applied unguarded — a freshly mounted page has no unsaved edits to protect.
  seed = null,
  beforeChange,
  // Runs when the merchant's own action changes the filter, once the guard
  // has let it through — never for the seed or a restored filter.
  onChange,
} = {}) {
  // Image-capable stores label a picked item by product name, not the GID-suffix
  // item_code (see formatItemLabel). Baked into the committed filter at commit time.
  const isShopify = useIsShopify();
  // `undefined` = this tab never held a filter; `null` = the merchant cleared
  // it. Only the first lets a seed in.
  const [stored, setCommitted] = useTabPersistedState(persistKey, undefined);
  const committed = stored === undefined ? null : stored;
  // Decided once, at mount: a state initializer runs on the first render only.
  const [mountSeed] = useState(() => (stored === undefined && seed ? itemCodeFilter(seed) : null));
  const [inputText, setInputText] = useState(() => filterDisplayText(mountSeed || committed));

  // Mount-only seed application; the state initializer above already put
  // the seed text in the box, this commits (and so persists) it as the active
  // filter.
  useEffect(() => {
    if (mountSeed) setCommitted(mountSeed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An item committed by code alone (a route-segment seed, or one restored
  // before its name arrived) is named by one request: it already filters, and
  // the box shows its label once the item is read.
  const unlabeledCode = committed?.kind === 'item' && committed.label == null
    ? committed.q : null;
  // The failed read, for the bar to say so — an empty box over an applied
  // filter would read as no filter at all. Kept with its code, so it stops
  // applying the moment the filter changes.
  const [labelFailure, setLabelFailure] = useState(null);
  const labelError = labelFailure && labelFailure.code === unlabeledCode
    ? labelFailure.error : null;
  useEffect(() => {
    if (!unlabeledCode) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await sessionApi.get(
          `/api/items/?item_code=${encodeURIComponent(unlabeledCode)}&page_size=1`,
          { signal: controller.signal },
        );
        if (!res.ok) throw await apiErrorFrom(res, 'the linked item');
        const [item] = (await res.json()).results;
        if (!item) throw new Error(`no live item carries the code ${unlabeledCode}`);
        const named = itemFilter(item, isShopify);
        setCommitted((prev) => (
          prev?.kind === 'item' && prev.q === unlabeledCode && prev.label == null ? named : prev
        ));
        // Only into an untouched box — never over what the merchant typed.
        setInputText((prev) => (prev === '' ? named.label : prev));
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('useItemFilter: failed to name item %s:', unlabeledCode, error);
        setLabelFailure({ code: unlabeledCode, error });
      }
    })();
    return () => controller.abort();
  }, [unlabeledCode, isShopify, setCommitted]);

  // While a beforeChange confirm dialog is open, freeze the draft: the
  // dialog owns the interaction, and a keystroke landing mid-guard would
  // desync the restore-on-cancel path.
  const guardPendingRef = useRef(false);

  const beforeChangeRef = useRef(beforeChange);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    beforeChangeRef.current = beforeChange;
    onChangeRef.current = onChange;
  });

  const guarded = useCallback(async (fn) => {
    if (!beforeChangeRef.current) { fn(); return true; }
    // A confirm dialog is already open for a previous action — drop this
    // one instead of stacking a second dialog. The user re-triggers after
    // answering the first prompt.
    if (guardPendingRef.current) return false;
    guardPendingRef.current = true;
    try {
      if (!(await beforeChangeRef.current())) return false;
    } finally {
      guardPendingRef.current = false;
    }
    fn();
    return true;
  }, []);

  const commit = useCallback((filter) => guarded(() => {
    setCommitted(filter);
    setInputText(filterDisplayText(filter));
    onChangeRef.current?.();
  }), [guarded, setCommitted]);

  const commitText = useCallback((text) => commit(textFilter(text)), [commit]);
  const commitItem = useCallback((item) => commit(itemFilter(item, isShopify)), [commit, isShopify]);
  const clear = useCallback(() => commit(null), [commit]);

  const onInputChange = useCallback((text) => {
    if (guardPendingRef.current) return;
    if (text === '' && committed) {
      // Deleting the last character drops the active filter (same action
      // as the X button) — an applied filter behind an empty box is the
      // invisible-filter bug this module exists to prevent.
      clear();
      return;
    }
    setInputText(text);
  }, [committed, clear]);

  // Suggestions. Skipped while the box shows the committed filter verbatim
  // (post-commit / post-restore) — the user isn't searching, and the popup
  // would just reopen over the grid.
  const { suggestions, loading, failed } = useItemSuggestions(inputText, {
    skip: inputText === filterDisplayText(committed),
  });

  // Memoized on the committed value so a page's params object, and the grid
  // refetch keyed on it, change only when the filter does.
  const scope = useMemo(() => filterScope(committed), [committed]);
  const params = useMemo(() => filterParams(committed), [committed]);

  return {
    filter: committed,
    // Scope slot for the charts (`useGridChartScope`).
    scope,
    // Query params for a grid listing and its export: `{search}`, `{item_code}`
    // or `{}`.
    params,
    displayText: filterDisplayText(committed),
    inputText,
    onInputChange,
    suggestions,
    loading,
    // The last suggestion request errored — the bar says so rather than
    // showing the merchant an empty list that reads as "no such item".
    suggestionsFailed: failed,
    // The read that names a code-seeded item failed (an Error, else null).
    labelError,
    commitText,
    commitItem,
    clear,
    guarded,
  };
}
